import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { Phone, MessageSquare, Mic, Plus, Flag, Clock } from 'lucide-react';
import { CapacitorSafePoller, makePhoneCall, sendSMS } from '../../../utils/capacitorHelper';
import VoiceCommandOverlay from './VoiceCommandOverlay';
import SmartPickupAssist from './SmartPickupAssist';
import RideChat from '../../rider/RideChat';
import useRideChatUnread from '../../../hooks/useRideChatUnread';
import { toast } from '../../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const mapContainerStyle = { width: '100%', height: '100dvh' };
const MAP_OPTS = {
  disableDefaultUI: true, zoomControl: false, streetViewControl: false,
  fullscreenControl: false, mapTypeControl: false, clickableIcons: false,
};

/**
 * Screens 4 & 5 — Active Trip.
 *  - status === 'accepted' | 'on_the_way' | 'arrived'  → Screen 4: en-route to rider
 *  - status === 'in_progress'                          → Screen 5: rider in car
 *  - status === 'completed'                            → defer to TripCompletionCard via onComplete
 *
 * Design follows the Bolt-style spec: full map, minimal floating UI,
 * thumb-reachable bottom card. Wait-fee chip appears when arrived.
 */
const ActiveTripScreen = ({ ride, onComplete, driverId }) => {
  const [status, setStatus] = useState(ride?.status || 'accepted');
  const [riderDetails, setRiderDetails] = useState(null);
  const [directions, setDirections] = useState(null);
  const [driverLoc, setDriverLoc] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [arrivedAt, setArrivedAt] = useState(null); // ISO when driver pressed "I've arrived"
  const [waitSecs, setWaitSecs] = useState(0); // seconds since arrived (wait fee starts after 2:00)
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const unreadChat = useRideChatUnread(ride?.id, 'driver', showChat, toast);

  const [eta, setEta] = useState(null);

  const isEnRoute = ['accepted', 'on_the_way', 'arrived'].includes(status);
  const isInProgress = status === 'in_progress';

  // --- Fetch rider info (masked phone for tap-to-call) ---
  useEffect(() => {
    if (!ride?.rider_id) return;
    fetch(`${API}/api/users/${ride.rider_id}`)
      .then((r) => r.json())
      .then(setRiderDetails)
      .catch(() => { /* ignore */ });
  }, [ride?.rider_id]);

  // --- Watch driver location ---
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setDriverLoc({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => { /* ignore */ },
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // --- Compute directions ---
  useEffect(() => {
    if (!ride || !window.google || !driverLoc) return;
    const ds = new window.google.maps.DirectionsService();
    const origin = isInProgress
      ? { lat: ride.pickup_location.latitude, lng: ride.pickup_location.longitude }
      : driverLoc;
    const destination = isInProgress
      ? { lat: ride.dropoff_location.latitude, lng: ride.dropoff_location.longitude }
      : { lat: ride.pickup_location.latitude, lng: ride.pickup_location.longitude };
    ds.route(
      { origin, destination, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, st) => {
        if (st === 'OK') {
          setDirections(result);
          const leg = result?.routes?.[0]?.legs?.[0];
          if (leg?.duration?.text) setEta(leg.duration.text);
        }
      }
    );
  }, [ride, driverLoc, isInProgress]);

  // --- Wait-fee timer once "I've arrived" was tapped ---
  useEffect(() => {
    if (!arrivedAt) return;
    const t = setInterval(() => {
      setWaitSecs((s) => s + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [arrivedAt]);

  // --- Status update helper ---
  const setRideStatus = async (next) => {
    setStatus(next); // optimistic
    try {
      await fetch(`${API}/api/rides/${ride.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
    } catch (_) { /* keep optimistic */ }
  };

  const handleArrived = async () => {
    setArrivedAt(new Date().toISOString());
    await setRideStatus('arrived');
  };

  const handleStartTrip = async () => {
    await setRideStatus('in_progress');
  };

  const handleEndTrip = async () => {
    try {
      const finalPrice = ride.estimated_price || 0;
      const r = await fetch(`${API}/api/rides/${ride.id}/complete?driver_id=${driverId}&final_price=${finalPrice}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (d?.success) {
        onComplete?.({ ride: d.ride || ride, charges: d.charges });
      }
    } catch (_) { /* ignore */ }
  };

  // --- Wait-fee label ---
  const waitFeeText = (() => {
    if (!arrivedAt) return null;
    const COUNTDOWN = 120; // 2 minutes free
    if (waitSecs < COUNTDOWN) {
      const remaining = COUNTDOWN - waitSecs;
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      return `Wait fee starts in ${m}:${s.toString().padStart(2, '0')}`;
    }
    const overSecs = waitSecs - COUNTDOWN;
    const m = Math.floor(overSecs / 60);
    return `Wait fee active · +${m + 1} min`;
  })();

  const riderFirstName = (riderDetails?.first_name || riderDetails?.firstName || ride?.rider_first_name || 'Rider').split(' ')[0];
  const dropoffShort = ride?.dropoff_location?.address?.split(',')[0] || 'destination';

  return (
    <div className="fixed inset-0 z-[60] bg-gray-100" data-testid="driver-active-trip">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={driverLoc || (ride?.pickup_location ? { lat: ride.pickup_location.latitude, lng: ride.pickup_location.longitude } : { lat: -13.96, lng: 33.77 })}
        zoom={15}
        onLoad={setMapRef}
        options={MAP_OPTS}
      >
        {ride?.pickup_location && (
          <Marker
            position={{ lat: ride.pickup_location.latitude, lng: ride.pickup_location.longitude }}
            icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
          />
        )}
        {ride?.dropoff_location && (
          <Marker
            position={{ lat: ride.dropoff_location.latitude, lng: ride.dropoff_location.longitude }}
            icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
          />
        )}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{ suppressMarkers: true, polylineOptions: { strokeColor: '#16A34A', strokeWeight: 5, strokeOpacity: 0.85 } }}
          />
        )}
      </GoogleMap>

      {/* Top floating pill */}
      <div className="fixed top-3 left-3 right-3 z-[65]" style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}>
        <div className="bg-white/95 backdrop-blur-md rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.12)] px-4 py-2.5 flex items-center justify-between">
          <p className="text-[14px] font-semibold text-gray-900 truncate" data-testid="active-trip-pill-title">
            {isEnRoute ? `Picking up ${riderFirstName}` : `Trip in progress — ${dropoffShort}`}
          </p>
          {eta && (
            <span className="ml-2 shrink-0 inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold tabular-nums">
              <Clock className="w-3 h-3" /> Arriving in {eta}
            </span>
          )}
        </div>
      </div>

      {/* Voice command chip — visible in both en-route and in-progress */}
      <button
        type="button"
        onClick={() => setShowVoicePanel(true)}
        data-testid="active-trip-voice-btn"
        className="fixed left-3 z-[64] inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/95 backdrop-blur-md text-gray-700 text-[12px] font-semibold shadow-md"
        style={{ top: 'calc(64px + env(safe-area-inset-top, 0))' }}
      >
        <Mic className="w-3.5 h-3.5" /> Voice on
      </button>

      {/* Wait fee chip — only after arrived */}
      {waitFeeText && (
        <div
          data-testid="active-trip-wait-chip"
          className="fixed right-3 z-[64] inline-flex items-center gap-1 px-3 h-9 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold shadow-md tabular-nums"
          style={{ top: 'calc(64px + env(safe-area-inset-top, 0))' }}
        >
          <Clock className="w-3 h-3" /> {waitFeeText}
        </div>
      )}

      {/* Bottom floating card */}
      {isEnRoute ? (
        <EnRouteBottomCard
          rider={riderDetails || { first_name: riderFirstName }}
          rideId={ride?.id}
          onArrived={handleArrived}
          onStartTrip={handleStartTrip}
          onMessage={() => setShowChat(true)}
          unread={unreadChat}
          arrived={!!arrivedAt}
        />
      ) : isInProgress ? (
        <InProgressBottomCard
          dropoffAddress={ride?.dropoff_location?.address}
          onEnd={handleEndTrip}
        />
      ) : null}

      {/* Smart pickup assist (only while heading to pickup) */}
      {isEnRoute && ride?.pickup_location?.latitude != null && (
        <SmartPickupAssist
          pickupLat={ride.pickup_location.latitude}
          pickupLng={ride.pickup_location.longitude}
          onClose={() => { /* auto-dismisses; caller doesn't need to do anything */ }}
        />
      )}

      {/* Voice command overlay (mock — phase 4 ships full implementation) */}
      {showVoicePanel && (
        <VoiceCommandOverlay
          onCommand={(cmd) => {
            if (cmd?.type === 'arrived' && isEnRoute && !arrivedAt) handleArrived();
            else if (cmd?.type === 'start_trip' && status === 'arrived') handleStartTrip();
            else if (cmd?.type === 'end_trip' && isInProgress) handleEndTrip();
            else if (cmd?.type === 'call' && riderDetails?.phone) makePhoneCall?.(riderDetails.phone);
            else if (cmd?.type === 'message' && riderDetails?.phone) sendSMS?.(riderDetails.phone, '');
          }}
          onClose={() => setShowVoicePanel(false)}
        />
      )}

      {/* In-app chat with the rider */}
      {showChat && (
        <RideChat
          rideId={ride?.id}
          senderId={driverId}
          senderRole="driver"
          recipientName={riderDetails?.name || riderDetails?.first_name || 'Rider'}
          recipientPhone={riderDetails?.phone}
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  );
};

// --- Screen 4: en route bottom card ---
const EnRouteBottomCard = ({ rider, rideId, onArrived, onStartTrip, onMessage, unread = 0, arrived }) => {
  const phone = rider?.phone || '';
  const initials = ((rider?.first_name || rider?.firstName || 'R')[0] + (rider?.last_name || rider?.lastName || '')[0]).toUpperCase();
  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-[65] bg-white/95 backdrop-blur-md rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] border border-gray-100 p-4"
      style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0))' }}
      data-testid="active-trip-bottom-card-enroute"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm flex items-center justify-center ring-2 ring-white">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-semibold text-gray-900 truncate">
            {rider?.first_name || rider?.firstName || 'Rider'} {rider?.last_name || rider?.lastName || ''}
          </p>
          <p className="text-[11px] text-gray-500">
            Trip ID · <span className="font-mono">{(rideId || '').slice(0, 8)}</span>
          </p>
        </div>
      </div>

      {/* Call + Message */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          onClick={() => makePhoneCall?.(phone)}
          data-testid="active-trip-call-btn"
          className="h-12 rounded-2xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold text-[13px] inline-flex items-center justify-center gap-2"
          aria-label="Call rider"
        >
          <Phone className="w-4 h-4" /> Call
        </button>
        <button
          type="button"
          onClick={() => onMessage?.()}
          data-testid="active-trip-msg-btn"
          className="relative h-12 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-[13px] inline-flex items-center justify-center gap-2"
          aria-label="Message rider"
        >
          {unread > 0 && (
            <span
              data-testid="active-trip-msg-unread-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white animate-pulse"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
          <MessageSquare className="w-4 h-4" /> Message
        </button>
      </div>

      {!arrived ? (
        <button
          type="button"
          onClick={onArrived}
          data-testid="active-trip-arrived-btn"
          className="w-full h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[16px] font-bold tracking-wide shadow-lg shadow-emerald-600/30 active:scale-[0.98] transition-all"
        >
          I've arrived
        </button>
      ) : (
        <button
          type="button"
          onClick={onStartTrip}
          data-testid="active-trip-start-btn"
          className="w-full h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[16px] font-bold tracking-wide shadow-lg shadow-blue-600/30 active:scale-[0.98] transition-all"
        >
          Start trip
        </button>
      )}
    </div>
  );
};

// --- Screen 5: in-progress bottom card (slim) ---
const InProgressBottomCard = ({ dropoffAddress, onEnd }) => {
  const [confirming, setConfirming] = useState(false);
  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-[65] bg-white/95 backdrop-blur-md rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] border border-gray-100 p-4"
      style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0))' }}
      data-testid="active-trip-bottom-card-progress"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-0.5">Heading to</p>
      <p className="text-[15px] font-semibold text-gray-900 truncate mb-3" data-testid="active-trip-dropoff">
        {dropoffAddress || 'Destination'}
      </p>
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-gray-100 text-gray-500 text-[11px] font-semibold opacity-60"
            aria-label="Add stop (coming soon)"
          >
            <Plus className="w-3.5 h-3.5" /> Add stop
          </button>
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-gray-100 text-gray-500 text-[11px] font-semibold opacity-60"
            aria-label="Report issue (coming soon)"
          >
            <Flag className="w-3.5 h-3.5" /> Report
          </button>
        </div>
        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            data-testid="active-trip-end-btn"
            className="px-4 h-10 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[12px] font-bold tracking-wide active:scale-95 transition-transform"
          >
            End trip
          </button>
        ) : (
          <div className="flex items-center gap-2" data-testid="active-trip-end-confirm">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="px-3 h-9 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onEnd}
              data-testid="active-trip-end-confirm-btn"
              className="px-4 h-9 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[12px] font-bold"
            >
              Confirm end
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveTripScreen;
